import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WifiIcon from "@material-ui/icons/Wifi";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { tNegocio } from "./shared";
import { useStyles } from "./styles";
import { formatDateTime } from "./helpers";

const emptyForm = () => ({
  enabled: false,
  payId: "",
  qrUrl: "",
  apiKey: "",
  apiSecret: "",
  apiKeySet: false,
  apiSecretSet: false,
  validatedAt: null,
});

export default function BinancePaySettings() {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/negocio/binance-pay");
      setForm({
        enabled: !!data.binancePayEnabled,
        payId: data.binancePayId || "",
        qrUrl: data.binanceQrUrl || "",
        apiKey: "",
        apiSecret: "",
        apiKeySet: !!data.apiKeySet,
        apiSecretSet: !!data.apiSecretSet,
        validatedAt: data.binanceValidatedAt || null,
      });
    } catch (e) {
      toastError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        payId: form.payId,
        qrUrl: form.qrUrl,
      };
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (form.apiSecret.trim()) payload.apiSecret = form.apiSecret.trim();
      const { data } = await api.put("/negocio/binance-pay", payload);
      setForm((s) => ({
        ...s,
        enabled: !!data.binancePayEnabled,
        payId: data.binancePayId || "",
        qrUrl: data.binanceQrUrl || "",
        apiKey: "",
        apiSecret: "",
        apiKeySet: !!data.apiKeySet,
        apiSecretSet: !!data.apiSecretSet,
        validatedAt: data.binanceValidatedAt || null,
      }));
      toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/negocio/binance-pay/test");
      setForm((s) => ({ ...s, validatedAt: data.validatedAt || new Date().toISOString() }));
      toast.success(tNegocio("pages.binance.testOk"));
    } catch (e) {
      toastError(e);
    } finally {
      setTesting(false);
    }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/files/upload-single", body);
      setForm((s) => ({ ...s, qrUrl: data.path || data.url || "" }));
      toast.success(tNegocio("pages.binance.qrUploaded"));
    } catch (e) {
      toastError(e);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div>
      <div className={classes.captionBlock} style={{ marginBottom: 16 }}>
        {tNegocio("pages.binance.hint")} {tNegocio("pages.binance.setup")}{" "}
        <a href="https://www.binance.com/en/my/settings/api-management" target="_blank" rel="noopener noreferrer">
          {tNegocio("pages.binance.apiLink")}
        </a>
        {" "}{tNegocio("pages.binance.assignHint")}
      </div>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!form.enabled}
            onChange={(e) => setForm((s) => ({ ...s, enabled: e.target.checked }))}
          />
        }
        label={tNegocio("pages.binance.enabled")}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.binance.apiKey")}
        type={showKey ? "text" : "password"}
        value={form.apiKey}
        onChange={(e) => setForm((s) => ({ ...s, apiKey: e.target.value }))}
        placeholder={form.apiKeySet ? tNegocio("pages.binance.keySet") : ""}
        helperText={tNegocio("pages.binance.apiKeyHint")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowKey((v) => !v)}>
                {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.binance.apiSecret")}
        type={showSecret ? "text" : "password"}
        value={form.apiSecret}
        onChange={(e) => setForm((s) => ({ ...s, apiSecret: e.target.value }))}
        placeholder={form.apiSecretSet ? tNegocio("pages.binance.keySet") : ""}
        helperText={tNegocio("pages.binance.apiSecretHint")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowSecret((v) => !v)}>
                {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.binance.payId")}
        value={form.payId}
        onChange={(e) => setForm((s) => ({ ...s, payId: e.target.value }))}
        helperText={tNegocio("pages.binance.payIdHint")}
      />
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginTop: 8 }}>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.binance.qrUrl")}
          value={form.qrUrl}
          onChange={(e) => setForm((s) => ({ ...s, qrUrl: e.target.value }))}
          style={{ flex: 1, minWidth: 220 }}
        />
        <Button variant="outlined" component="label" style={{ marginTop: 8 }}>
          {tNegocio("pages.binance.uploadQr")}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => uploadQr(e.target.files?.[0])}
          />
        </Button>
      </div>
      {form.qrUrl ? (
        <div style={{ marginTop: 12 }}>
          <img
            src={form.qrUrl}
            alt="Binance Pay QR"
            style={{ maxWidth: 180, maxHeight: 180, borderRadius: 8, border: "1px solid #ddd" }}
          />
        </div>
      ) : null}
      {form.validatedAt ? (
        <div className={classes.captionBlock} style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <CheckCircleIcon style={{ fontSize: 16, color: "#2e7d32" }} />
          {tNegocio("pages.binance.validatedAt").replace("{date}", formatDateTime(form.validatedAt))}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
        <Button color="primary" variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : tNegocio("save")}
        </Button>
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={16} /> : <WifiIcon />}
          onClick={test}
          disabled={testing || (!form.apiKeySet && !form.apiKey)}
        >
          {tNegocio("pages.binance.test")}
        </Button>
      </div>
    </div>
  );
}
